import { usePathname } from 'next/navigation'
import { useTranslation } from 'next-i18next'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { STATSIG_EXPERIMENTS_CONFIG } from '@src/config/statsig-experiments.config'
import { STATSIG_EXPERIMENTS, useStatsigExperiment } from '@src/experiments/statsig-integration'
import { CTA_TYPE } from '@src/quince-components/src/atoms/productCTA/productCTAConstants'
import { useGoldBarProductEligibility } from '@src/quince-components/src/atoms/productCTA/useGoldBarProductEligibility'
import useProductCTAType from '@src/quince-components/src/atoms/productCTA/useProductCTAType'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { ProductContext } from '@src/quince-site-common/src/contexts/product-context/product-context-provider.helper'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductOption, ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import { IProductPurchaseInfo } from '@src/quince-site-common/src/domains/ProductPurchaseInfo'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper, {
  CUSTOM_FIELD_NAMES,
  getFormattedCustomFieldText,
} from '@src/quince-site-common/src/helpers/productHelper'
import { useWaitList } from '@src/quince-site-common/src/hooks/useWaitList'
import { useZipCode } from '@src/quince-site-common/src/hooks/zipcode'
import productBuyNowStateManager from '@src/quince-site-common/src/redux/globalStateManager/ProductBuyNowStateManager'
import { getProductEDDDetails } from '@src/quince-site-common/src/services/eddService/eddService'
import { EDDProductType } from '@src/quince-site-common/src/services/eddService/eddService.types'
import productService from '@src/quince-site-common/src/services/productService'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { ANALYTIC_COOKIES } from '@src/quince-site-common/src/utils/constants'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import validateEmail from '@src/quince-site-common/src/utils/validateEmail'
import { getUpdatedSelectedVariantPurchaseInfo } from '@src/quince-site-pdp/src/molecules/productFormWrapper/productFormWrapper.helper'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { findExactOrContainingHandleGroupItem } from '../productPurchase/productPurchase.helpers'

export const useMiniPDPProductCTA = ({
  product,
  selectedOptions,
  productOptions,
}: {
  product: Product
  selectedOptions: ProductVariantOption[]
  productOptions: ProductOption[]
}) => {
  /* This code is copied from ProductFormWrapper and ProductForm */
  const [productPurchaseInfo, setProductPurchaseInfo] = useState<IProductPurchaseInfo | undefined>(undefined)
  const [isProductWaitListed, setIsProductWaitListed] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [currentSelectedOption, setCurrentSelectedOption] = useState<string | null>(null)
  const [email, setEmail] = useState('')

  const { t } = useTranslation('common')

  const pathname = usePathname()
  const { zipCode } = useZipCode()
  const { isUserAlreadyWaitListed, addUserToWaitList } = useWaitList()
  const [eddDetails, setEddDetails] = useState<EDDProductType>({})
  const { selectedVariantPurchaseInfo, setSelectedVariantPurchaseInfo } = useContext(ProductContext)
  const { experimentBucket: eddExpBucket, init: eddExpInit } = useStatsigExperiment(STATSIG_EXPERIMENTS.EDD)
  const productCTAType = useProductCTAType({
    selectedVariantPurchaseInfo: selectedVariantPurchaseInfo,
    selectedOptions: selectedOptions,
    productOptions: productOptions,
  })
  const { isGoldBarError } = useGoldBarProductEligibility(product, setErrorMessage)

  const isEDDExpEnabled = eddExpBucket !== STATSIG_EXPERIMENTS_CONFIG[STATSIG_EXPERIMENTS.EDD].buckets?.control
  // for single variant or for products which already have a variant selected
  //this only checks if there are any options selected initially for event data
  const fireVariantSelectedEventForInitialSelectedOption = useRef(!!(selectedOptions.slice(-1)[0]?.name ?? null))
  const selectedSiblingProductId = product.addOnProductOptionGroups?.[0]?.groupItems
    ? findExactOrContainingHandleGroupItem(product.addOnProductOptionGroups[0].groupItems, pathname)?.spid
    : undefined

  useEffect(() => {
    productService
      .getProductPurchaseInfo(product)
      .then(purchaseInfo => {
        setProductPurchaseInfo(purchaseInfo)
      })
      .catch((err: unknown) => {
        captureException(err, {
          module: MODULES.MINI_PDP,
          feature: FEATURES.UNKNOWN,
        })
      })
  }, [product.id, product.childSpids])

  useEffect(() => {
    if (!zipCode || !eddExpInit) return
    getProductEDDDetails(zipCode, [product])
      .then(eddInfo => {
        setEddDetails(eddInfo.product_variants[product.id] || {})
      })
      .catch((err: unknown) => {
        captureException(err, {
          module: MODULES.MINI_PDP,
          feature: FEATURES.UNKNOWN,
        })
      })
  }, [zipCode, product.id, eddExpBucket, eddExpInit])

  // Setting Product purchase meta info for selected options.
  useEffect(() => {
    setIsProductWaitListed(isUserAlreadyWaitListed(product, selectedOptions))
    setSelectedVariantPurchaseInfo?.(undefined)

    const updatedSelectedVariantPurchaseInfo = getUpdatedSelectedVariantPurchaseInfo({
      productPurchaseInfo,
      product: product,
      selectedOptions: selectedOptions,
      eddDetails,
      zipCode: zipCode ?? undefined,
      isProductWaitListed,
      currentSelectedOption,
      setCurrentSelectedOption,
      fireVariantSelectedEventForInitialSelectedOption,
      selectedSiblingProductId: selectedSiblingProductId,
      updatedProductOptions: product.productOptions,
    })

    setSelectedVariantPurchaseInfo?.(updatedSelectedVariantPurchaseInfo)
  }, [
    product.id,
    productPurchaseInfo,
    selectedOptions,
    isProductWaitListed,
    eddDetails,
    eddExpBucket,
    currentSelectedOption,
  ])

  const isMadeToOrder = product
    ? getFormattedCustomFieldText(
        productHelper.getCustomFieldValue(product, CUSTOM_FIELD_NAMES.MADE_TO_ORDER_TEXT) ?? '',
      )
    : false

  const selectedVariantAvailableInventory = selectedVariantPurchaseInfo?.availableInventory ?? 0
  const showVariantAvailableInventory =
    ![CTA_TYPE.WAITLIST, CTA_TYPE.PREORDER].includes(productCTAType) &&
    selectedVariantAvailableInventory > 0 &&
    selectedVariantAvailableInventory < 5
  const isNonPhysicalProduct = product
    ? productHelper.getIsTruthyCustomField(product, CUSTOM_FIELD_NAMES.IS_NON_PHYSICAL_PRODUCT)
    : false
  const areAllVariantOOS = useMemo(() => {
    if (productPurchaseInfo?.svidVsSvDetails) {
      const { svidVsSvDetails } = productPurchaseInfo
      return (
        Object.keys(svidVsSvDetails).findIndex(
          svid => (svidVsSvDetails[svid as unknown as number]?.quantityAvailable || 0) > 0,
        ) === -1
      )
    }
    return true
  }, [productPurchaseInfo])

  const showInStockAndReadyToShip =
    selectedVariantPurchaseInfo?.isAnySelectedVariantInStock &&
    !showVariantAvailableInventory &&
    !selectedVariantPurchaseInfo?.isPreorder &&
    !isMadeToOrder &&
    !areAllVariantOOS &&
    !isGoldBarError &&
    !isNonPhysicalProduct &&
    !isEDDExpEnabled &&
    eddExpInit

  const onJoinTheWaitList = useCallback(
    (providedEmail?: string) => {
      if (selectedVariantPurchaseInfo?.hasJoinedWaitList) {
        return
      }

      if (AuthUtil.isLoggedIn()) {
        const loggedInEmail =
          getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL) ?? getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_TENTATIVE_EMAIL) ?? ''
        addUserToWaitList(product, selectedOptions, loggedInEmail)
        setIsProductWaitListed(true)
      } else {
        const emailToUse = providedEmail || email
        if (validateEmail(emailToUse)) {
          addUserToWaitList(product, selectedOptions, emailToUse)
          setIsProductWaitListed(true)

          setErrorMessage('')
        } else {
          setErrorMessage(t('please_enter_valid_email_address'))
        }
      }
    },
    [selectedOptions, email, selectedVariantPurchaseInfo?.hasJoinedWaitList],
  )
  /* End of copied code. */

  const onAddToCart = () => {
    const variants = productHelper.getVariantsForOptions(product, selectedOptions)
    const selectedVariant = variants[0]
    trackEvent(TELEMETRY_EVENT_NAMES.ATC_LINK_CLICKED, {
      context: {
        productProperties: getProductPropertiesForTracking(product, selectedVariant?.svid?.toString(), {}),
      },
    })
    productBuyNowStateManager.showBuyNowPopUp({
      product: product,
      selectedOptions: selectedOptions,
    })
  }

  // Only show waitlist CTA for single size products
  const showWaitlistCTA =
    productCTAType === CTA_TYPE.WAITLIST &&
    (product.productOptions.find(option => option.name === ProductOptionName.Size)?.values?.length ?? 0) <= 1

  return {
    productPurchaseInfo,
    selectedVariantPurchaseInfo,
    onJoinTheWaitList,
    onAddToCart,
    showInStockAndReadyToShip,
    errorMessage,
    setEmail,
    showWaitlistCTA,
    productCTAType,
  }
}
